/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import DS from 'ember-data';
import { dropTask } from 'ember-concurrency';
import DomainModel from 'cio/models/domain';
import IdentityModel from 'cio/models/identity';
import FormValidations from 'cio/utils/form-validations';
import ApiErrorsService from 'cio/services/api-errors';
import { DomainValidations } from 'cio/components/add-domain';
import { groupIdentitiesByDomain } from 'cio/utils/group-identities-by-domain';
import isDestroyed from 'cio/utils/is-destroyed';

interface SetupGuideChannelsEmailSendingDomainsArgs {
  domains: DomainModel[];
  identities: IdentityModel[];
  identitiesInUse: IdentityModel[];
  readOnly: boolean;
  onAddDomain: () => unknown;
  onDeleteDomain: () => unknown;
}

export default class SetupGuideChannelsEmailSendingDomains extends Component<SetupGuideChannelsEmailSendingDomainsArgs> {
  @service store!: DS.Store;
  @service apiErrors!: ApiErrorsService;

  @tracked isAddingDomain = false;

  @tracked domain: DomainModel;
  @tracked deletingDomain?: DomainModel;

  validations: FormValidations;

  get hasDynamicIdentities() {
    return this.args.identities.any((identity) => identity.hasDynamicEmail);
  }

  get usedIdentitiesByDomain() {
    return groupIdentitiesByDomain(this.args.identitiesInUse, this.args.domains);
  }

  get domains() {
    return this.args.domains.filter((domain) => !domain.isNew);
  }

  @action
  toggleConfirmingDelete(domain: DomainModel) {
    if (domain instanceof DomainModel && this.deletingDomain !== domain) {
      this.deletingDomain = domain;
    } else {
      this.deletingDomain = undefined;
    }
  }

  @action
  toggleAddingDomain() {
    if (this.isAddingDomain) {
      this.hideForm();
    } else {
      this.showForm();
    }
  }

  @action
  handleKeyUp({ key }: { key: KeyboardEvent['key'] }) {
    if (key === 'Escape') {
      this.isAddingDomain = false;
    }
  }

  @action
  onDomainChange(domain: string) {
    this.domain.set('domain', domain);
    this.validations.validateAttribute('domain');
  }

  constructor(owner: unknown, args: SetupGuideChannelsEmailSendingDomainsArgs) {
    super(owner, args);

    this.domain = this.store.createRecord('domain');

    this.validations = FormValidations.create({
      validations: DomainValidations,
      context: this,
      modelKey: 'domain',
    });
  }

  willDestroy() {
    super.willDestroy();

    // can happen in tests:
    if (!isDestroyed(this.domain)) {
      this.domain.unloadRecord();
    }
  }

  addDomainTask = dropTask(async (ev: Event) => {
    ev.preventDefault();

    if (!this.domain) {
      return;
    }

    await this.validations.validateModel();

    try {
      await this.domain.save();
      this.hideForm();
      this.args.onAddDomain();
    } catch (e) {
      // Validation errors are handled by FormValidations
      if (e.errors[0]?.status !== '422' && e.errors[0]?.source == null) {
        this.apiErrors.report(e);
      }
    }
  });

  deleteDomainTask = dropTask(async (ev: Event) => {
    ev.preventDefault();

    if (!this.deletingDomain) {
      return;
    }

    try {
      await this.deletingDomain.destroyRecord();
      this.deletingDomain = undefined;
      this.args.onDeleteDomain();
    } catch (err) {
      this.apiErrors.report(err);
      this.deletingDomain?.rollbackAttributes();
    }
  });

  private showForm() {
    this.isAddingDomain = true;
    this.domain = this.store.createRecord('domain');
  }

  private hideForm() {
    this.isAddingDomain = false;
    this.domain.rollbackAttributes();
  }
}
